import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { listClasses } from '../academyApi';
import { AcademyButton, compactInputBase, DateInput } from '../controls';
import NotesPanel from '../detail/NotesPanel';
import { todayKST } from '../periodDates';
import { Box, ErrorNotice, Spinner, useAcademyVersion, useAsync } from '../ui';
import { validDate } from './reportModel';

function rangeError(from, to, today) {
  if (!from && !to) return null;
  if (!validDate(from) || !validDate(to)) return '시작일과 종료일을 함께 입력해 주세요.';
  if (from > to) return '종료일은 시작일과 같거나 뒤여야 합니다.';
  if (to > today) return '오늘까지 조회할 수 있습니다.';
  return null;
}

function NotesDateRange({ from, to, today, onApply }) {
  const [draft, setDraft] = useState({ from: from || '', to: to || '' });
  const error = rangeError(draft.from, draft.to, today);
  const monthStart = `${today.slice(0, 7)}-01`;
  return <form className="space-y-2" onSubmit={(event) => { event.preventDefault(); if (!error) onApply(draft); }}>
    <div role="group" aria-label="메모 조회 기간" className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-gray-500">조회 기간</span>
      {[
        ['전체 기간', '', ''], ['오늘', today, today], ['이번 달', monthStart, today],
      ].map(([label, start, end]) => {
        const selected = (from || '') === start && (to || '') === end;
        return <AcademyButton key={label} type="button" variant={selected ? 'primary' : 'primary-outline'} aria-pressed={selected} onClick={() => onApply({ from: start, to: end })}>{label}</AcademyButton>;
      })}
    </div>
    <div className="flex flex-wrap items-end gap-2">
      <label className="space-y-1 text-xs text-gray-500"><span className="block">시작일</span><DateInput aria-label="메모 시작일" value={draft.from} max={today} compact fullWidth={false} onChange={(value) => setDraft({ ...draft, from: value })} /></label>
      <label className="space-y-1 text-xs text-gray-500"><span className="block">종료일</span><DateInput aria-label="메모 종료일" value={draft.to} max={today} compact fullWidth={false} onChange={(value) => setDraft({ ...draft, to: value })} /></label>
      <AcademyButton type="submit" aria-label="메모 기간 조회" disabled={!!error}>기간 조회</AcademyButton>
    </div>
    {error && <p role="alert" className="text-xs text-danger">{error}</p>}
  </form>;
}

export default function NotesReport({ filters, onChange }) {
  const { getAccessToken } = useAuth();
  const version = useAcademyVersion();
  const classes = useAsync((signal) => listClasses({ state: 'all', signal }, getAccessToken), `note-target-classes-${version}`);
  const today = todayKST();
  const classId = /^\d+$/.test(filters.note_class || '') && Number(filters.note_class) > 0 ? Number(filters.note_class) : null;
  const academyOnly = filters.note_class === 'academy';
  const page = /^\d+$/.test(filters.note_page || '') ? Math.max(1, Number(filters.note_page)) : 1;
  const from = filters.note_from || undefined;
  const to = filters.note_to || undefined;
  const invalidRange = rangeError(from, to, today);
  const exists = classId == null || classes.data?.some((cls) => cls.class_id === classId);
  const className = classes.data?.find((cls) => cls.class_id === classId)?.name;
  const target = classId ? { classId } : academyOnly ? { academy: true } : { allAcademy: true };
  const title = classId ? `${className || '반'} 메모` : academyOnly ? '학원 메모' : '전체 메모';
  const applyRange = (range) => onChange({ note_from: range.from || null, note_to: range.to || null, note_page: null });
  return <>
    <Box title="조회 조건">
      <div className="space-y-3">
        <label className="flex flex-wrap items-center gap-2 text-xs text-gray-500">대상
          <select aria-label="메모 대상" className={`${compactInputBase} max-w-full`} value={classId || (academyOnly ? 'academy' : '')} onChange={(event) => onChange({ note_class: event.target.value || null, note_page: null })}>
            <option value="">전체(학원·모든 반)</option>
            <option value="academy">학원</option>
            {classId && !exists && <option value={classId}>{classes.loading ? '반 확인 중…' : '선택한 반'}</option>}
            {(classes.data || []).map((cls) => <option key={cls.class_id} value={cls.class_id}>{cls.name}</option>)}
          </select>
        </label>
        <NotesDateRange key={`${from || ''}-${to || ''}`} from={from} to={to} today={today} onApply={applyRange} />
      </div>
    </Box>
    {classes.error && <ErrorNotice error={classes.error} onRetry={classes.reload} compact />}
    {classId && classes.loading && !exists && <Spinner label="반 확인 중" compact />}
    {classId && classes.data && !exists ? <p role="alert" className="text-xs text-danger">선택한 반을 찾을 수 없습니다. 조회 대상을 다시 선택해 주세요.</p>
      : !invalidRange && exists && <NotesPanel key={`${classId || (academyOnly ? 'academy' : 'all')}-${page}-${from || ''}-${to || ''}`} target={target} collapsibleComposer composeTitle={`새 메모 · ${className || '학원'}`} from={from} to={to} page={page} onPageChange={(value) => onChange({ note_page: String(value) })} title={title} sub={`${from ? `${from} ~ ${to}` : '전체 기간'} · 고정 먼저`} />}
  </>;
}
